import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FaPlus, FaEllipsisV, FaGlobe, FaArrowLeft, FaTimes, FaChevronUp, FaChevronDown, FaDatabase, FaBookmark, FaComment, FaTable, FaSave, FaTrash, FaMapMarkerAlt } from 'react-icons/fa';
import "./sidebarmenu.css";
import logo from '../../assets/img/logo-data-new.png';
import graficasBI from '../../assets/img/graficas-BI.png';
import { useAuthenticator } from '@aws-amplify/ui-react';
import logoChat from '../../assets/img/logo-chat.png';

import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";

import config from '../../amplifyconfiguration.json';

const UsuariosLink = process.env.REACT_APP_USUARIOS_LINK;
const baseURL = process.env.REACT_APP_BASE_URL;
const endpoint_get = '/get_conversations_user';
const endpoint_add = '/add_conversation';
const endpoint_delete = '/delete_conversation';
// Nuevos endpoints para consultas guardadas
const endpoint_get_saved_consults = '/get_saved_consults';
const endpoint_add_saved_consult = '/add_saved_consult';
const endpoint_delete_consult = '/delete_consult';

// Componente para el elemento de una conversación
const ConversationLink = ({ id_conversation, description, handleDelete, type_name, isActive }) => {
  const navigate = useNavigate();

  const handleConversationClick = () => {
    const path = type_name === "geo" ? `/geo/${id_conversation}` : `/chat/${id_conversation}`;
    navigate(path);
  };

  // Función simple para manejar la eliminación
  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Evita que se navegue a la conversación
    handleDelete(id_conversation, description);
  };

  return (
    <div
      className={`sidebar-link ${isActive ? 'active' : ''}`}
      onClick={handleConversationClick}
    >
      {type_name === "geo" ? (
        <FaMapMarkerAlt size={14} style={{ marginRight: '10px', color: '#073C5C' }} />
      ) : (
        <img src={logoChat} alt="Logo Chat" className="logo-chat" />
      )}
      <span className="conversation-title">{description}</span>

      {/* Botón de tres puntos que solo se muestra cuando está activo */}
      <button
        className="delete-conversation-btn"
        onClick={handleDeleteClick}
        title="Opciones"
      >
        <FaEllipsisV size={14} />
      </button>
    </div>
  );
};

// Componente para el encabezado de acordeón
const AccordionHeader = ({ title, icon, isOpen, onClick }) => {
  return (
    <div className="accordion-header" onClick={onClick}>
      {icon}
      <span className="accordion-title">{title}</span>
      {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
    </div>
  );
};

// Componente modificado para un elemento de consulta guardada
const SavedQueryItem = ({ id_saved, consult, onSelect, onDelete }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onSelect === 'function') {
      onSelect(consult);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof onDelete === 'function') {
      onDelete(id_saved, consult);
    }
  };

  return (
    <div
      className="sidebar-link saved-query"
      onClick={handleClick}
    >
      <FaBookmark size={14} className="query-icon" />
      <span className="query-title">{consult.length > 40 ? consult.substring(0, 40) + '...' : consult}</span>

      {/* Botón de tres puntos para eliminar */}
      <button
        className="delete-conversation-btn"
        onClick={handleDeleteClick}
        title="Eliminar consulta"
      >
        <FaEllipsisV size={14} />
      </button>
    </div>
  );
};

// Componente para un elemento del diccionario de datos
const DataDictionaryItem = ({ tableName, description, columns, isOpen, toggleTable }) => {
  return (
    <div className="data-dictionary-item">
      <div className="table-header" onClick={() => toggleTable(tableName)}>
        <FaTable size={14} className="table-icon" />
        <span className="table-name">{tableName}</span>
        {isOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </div>

      {isOpen && (
        <div className="table-columns custom-scrollbar">
          <div className="table-description">{description}</div>
          <div className="columns-container">
            {columns.map((column, index) => (
              <div key={index} className="column-info">
                <span className="column-name">{column.name}</span>
                <span className="column-type">{column.type}</span>
                {column.description && (
                  <span className="column-description">{column.description}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarMenu = ({ isSidebarVisible, toggleSidebar, setMessage, messageInputRef }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedConversationName, setSelectedConversationName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Estados para consultas guardadas
  const [savedQueries, setSavedQueries] = useState([]);
  const [isLoadingQueries, setIsLoadingQueries] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [modalType, setModalType] = useState('conversation'); // 'conversation' o 'query'

  // Estados para los acordeones
  const [openAccordion, setOpenAccordion] = useState('conversations');
  const [openTable, setOpenTable] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Obtener la conversación activa de la URL
  const currentPath = location.pathname;
  const activeChatId = currentPath.includes('/chat/')
    ? currentPath.split('/chat/')[1]
    : (currentPath.includes('/geo/') ? currentPath.split('/geo/')[1] : null);

  // Filtrar conversaciones por tipo
  const normalConversations = conversations.filter(conv => conv.type_name !== "geo");
  const geoConversations = conversations.filter(conv => conv.type_name === "geo");

  // Datos para el diccionario (hardcoded basado en las tablas proporcionadas)
  // Datos para el diccionario (hardcoded basado en la nueva tabla sales_data)
const dataDictionary = [
  {
    tableName: "public.my_data",
    description: "Tabla principal que almacena los datos internos de ventas de Dollar General, incluyendo información detallada de tiendas, productos, ventas netas, unidades vendidas y precios. Contiene múltiples registros por período temporal para el mismo producto y tienda, permitiendo análisis de rendimiento por ubicación y categoría.",
    columns: [
      { name: "year", type: "text", description: "Año de la venta (ej: '2024')" },
      { name: "month", type: "text", description: "Mes de la venta en formato YYYY-MM (ej: '2024-01', '2024-12')" },
      { name: "week", type: "text", description: "Semana de la venta en formato YYYY-WW (ej: '2024-01', '2024-52')" },
      { name: "date", type: "date", description: "Fecha exacta de la transacción de venta" },
      { name: "store_id", type: "integer", description: "Identificador único de la tienda Dollar General" },
      { name: "desc_tienda", type: "text", description: "Descripción o nombre de la tienda Dollar General" },
      { name: "category", type: "text", description: "Categoría principal del producto (ej: 'Food & Snacks', 'Health & Beauty')" },
      { name: "sku_client", type: "integer", description: "SKU único del producto usado por Dollar General" },
      { name: "item_desc", type: "text", description: "Descripción detallada del producto" },
      { name: "net_sales", type: "real", description: "Ventas netas en dólares del producto para el período" },
      { name: "units", type: "real", description: "Número de unidades vendidas del producto" },
      { name: "final_price", type: "real", description: "Precio final de venta del producto en Dollar General" }
    ]
  },
  {
    tableName: "public.competitors_data",
    description: "Tabla que almacena información de inteligencia competitiva del mercado, incluyendo precios, marcas, competidores y indicadores de actividad de ventas. Utilizada para análisis de posicionamiento competitivo y estrategias de precios de Dollar General frente a otros retailers.",
    columns: [
      { name: "year", type: "text", description: "Año del dato competitivo (ej: '2024')" },
      { name: "month", type: "text", description: "Mes del dato en formato YYYY-MM (ej: '2024-01', '2024-12')" },
      { name: "week", type: "text", description: "Semana del dato en formato YYYY-WW (ej: '2024-01', '2024-52')" },
      { name: "date", type: "date", description: "Fecha exacta de la observación competitiva" },
      { name: "competitor", type: "text", description: "Nombre del competidor o cadena retail competidora" },
      { name: "category", type: "text", description: "Categoría del producto para comparación competitiva" },
      { name: "brand", type: "text", description: "Marca del producto en el mercado competitivo" },
      { name: "upc", type: "integer", description: "Código UPC del producto para identificación universal" },
      { name: "item_desc", type: "text", description: "Descripción del producto competitivo" },
      { name: "price", type: "real", description: "Precio regular del competidor antes de promociones" },
      { name: "sales_flag", type: "real", description: "Indicador numérico de actividad promocional o ventas especiales" },
      { name: "final_price", type: "real", description: "Precio final del competidor incluyendo promociones y descuentos" },
      { name: "sku_client", type: "integer", description: "SKU correspondiente de Dollar General para comparación directa" }
    ]
  }
];


  // Función para alternar entre acordeones
  const toggleAccordion = (accordionName) => {
    setOpenAccordion(openAccordion === accordionName ? null : accordionName);
  };

  // Función para alternar la visualización de tablas
  const toggleTable = (tableName) => {
    setOpenTable(openTable === tableName ? null : tableName);
  };

  // Función para seleccionar una consulta guardada
  const handleSelectQuery = (query) => {
    console.log("Consulta seleccionada:", query);

    // 1. Intentar usar la función setMessage si está disponible
    if (typeof setMessage === 'function') {
      setMessage(query);
      console.log("Método 1: setMessage llamado con:", query);
    }

    // 2. Emitir un evento personalizado como respaldo
    try {
      // Crear un evento personalizado con los datos de la consulta
      const customEvent = new CustomEvent('savedQuerySelected', {
        detail: { query: query },
        bubbles: true,
        cancelable: true
      });

      // Disparar el evento desde el documento
      document.dispatchEvent(customEvent);
      console.log("Método 2: Evento personalizado emitido con:", query);

      // 3. Como último recurso, intentar actualizar directamente el textarea
      setTimeout(() => {
        const textareaElement = document.querySelector('.message-input');
        if (textareaElement) {
          textareaElement.value = query;

          // Disparar un evento de input para activar los listeners de React
          const inputEvent = new Event('input', { bubbles: true });
          textareaElement.dispatchEvent(inputEvent);

          // Ajustar la altura y dar foco
          textareaElement.style.height = 'auto';
          textareaElement.style.height = (textareaElement.scrollHeight) + 'px';
          textareaElement.focus();

          console.log("Método 3: Input actualizado directamente:", query);
        } else {
          console.warn("Textarea no encontrado en el DOM");
        }
      }, 100); // Pequeño retraso para asegurar que el DOM esté actualizado
    } catch (error) {
      console.error("Error al manejar la consulta guardada:", error);
    }

    // Opcional: cerrar el sidebar en versión móvil
    if (window.innerWidth < 768 && toggleSidebar) {
      toggleSidebar();
    }
  };

  const createAuthenticatedRequest = async (config) => {
    const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

    const credentials = fromCognitoIdentityPool({
      clientConfig: { region: config.aws_project_region },
      identityPoolId: config.aws_cognito_identity_pool_id,
      logins: { [my_login]: authToken }
    });

    const signer = new SignatureV4({
      credentials: credentials,
      region: config.aws_project_region,
      service: 'execute-api',
      sha256: Sha256
    });

    return async (endpoint, method = 'GET', body = null) => {
      const url = new URL(endpoint);

      const request = new HttpRequest({
        hostname: url.hostname,
        path: url.pathname + url.search,
        method: method,
        headers: {
          'Content-Type': 'application/json',
          host: url.hostname
        },
        body: body ? JSON.stringify(body) : undefined
      });

      const signedRequest = await signer.sign(request);

      const fetchOptions = {
        method: signedRequest.method,
        headers: signedRequest.headers,
        body: signedRequest.body
      };

      const response = await fetch(url.toString(), fetchOptions);
      return response.json();
    };
  };

  // Función para obtener consultas guardadas
  const fetchSavedQueries = async () => {
    if (user) {
      try {
        setIsLoadingQueries(true);
        const authenticatedRequest = await createAuthenticatedRequest(config);
        const url = `${baseURL}${endpoint_get_saved_consults}?id_user=${encodeURIComponent(user.username)}`;

        const response = await authenticatedRequest(url, 'GET', null);

        if (response && response.saved_consults) {
          // Ordenar consultas por fecha
          const sortedQueries = [...response.saved_consults].sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
          });

          setSavedQueries(sortedQueries);
        } else {
          console.warn('No saved consults data in response:', response);
          setSavedQueries([]);
        }
      } catch (error) {
        console.error('Error fetching saved consults:', error);
        setSavedQueries([]);
      } finally {
        setIsLoadingQueries(false);
      }
    }
  };

  // Función para guardar una consulta actual
  const saveCurrentQuery = async (query) => {
    if (!user || !query) return;

    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_add_saved_consult}`;

      const response = await authenticatedRequest(
        url,
        'POST',
        {
          id_user: user.username,
          consult: query
        }
      );

      if (response && response.id_saved) {
        // Actualizar la lista de consultas guardadas
        fetchSavedQueries();

        // Opcional: mostrar confirmación
        console.log('Consulta guardada con éxito');
      } else {
        console.error('Error al guardar la consulta:', response);
      }
    } catch (error) {
      console.error('Error saving query:', error);
    }
  };

  useEffect(() => {
    const fetchConversations = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const authenticatedRequest = await createAuthenticatedRequest(config);
          const url = `${baseURL}${endpoint_get}?id_user=${encodeURIComponent(user.username)}`;

          const response = await authenticatedRequest(url, 'GET', null);

          if (response && response.conversations) {
            // Ordenar conversaciones por fecha
            const sortedConversations = [...response.conversations].sort((a, b) => {
              return b.id_conversation - a.id_conversation;
            });

            setConversations(sortedConversations);
          } else {
            console.warn('No conversations data in response:', response);
            setConversations([]);
          }
        } catch (error) {
          console.error('Error completo:', error);
          setConversations([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchConversations();
    fetchSavedQueries(); // Cargar las consultas guardadas
  }, [user]);

  // Función para manejar la eliminación de una consulta guardada
  const handleDeleteQuery = (id_saved, consult) => {
    setSelectedQuery(id_saved);
    setSelectedConversationName(consult.substring(0, 30) + (consult.length > 30 ? '...' : ''));
    setModalType('query');
    setShowModal(true);
  };

  // Función para confirmar la eliminación de una consulta guardada
  const deleteQuery = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_delete_consult}`;

      const response = await authenticatedRequest(
        url,
        'DELETE',
        {
          id_saved: selectedQuery
        }
      );

      if (response) {
        // Actualizar el estado local
        setSavedQueries(prevQueries =>
          prevQueries.filter(query => query.id_saved !== selectedQuery)
        );

        // Cerrar el modal
        setShowModal(false);
        setSelectedQuery(null);
        setSelectedConversationName("");
      }
    } catch (error) {
      console.error('Error deleting saved query:', error);
    }
  };

  const addConversation = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_add}`;

      const response = await authenticatedRequest(
        url,
        'POST',
        {
          id_user: user.username,
          type_name: "text"
        }
      );

      if (response && response.id_conversation) {
        navigate(`/chat/${response.id_conversation}`);

        // Actualizar la lista de conversaciones
        const newConversation = {
          id_conversation: response.id_conversation,
          description: response.description || "Nueva consulta",
          type_name: "text"
        };

        setConversations(prev => [newConversation, ...prev]);
      } else {
        console.error('No se recibió id_conversation en la respuesta:', response);
      }
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  // Agregar este useEffect después de los otros useEffects existentes
  useEffect(() => {
    // Listener para actualizar las consultas guardadas cuando se guarda una nueva
    const handleQuerySaved = () => {
      console.log("Evento de consulta guardada recibido, actualizando lista...");
      fetchSavedQueries(); // Refrescar la lista desde el servidor
      setOpenAccordion('savedQueries'); // Abrir el acordeón de consultas guardadas
    };

    // Registrar el listener
    document.addEventListener('querySaved', handleQuerySaved);

    // Limpieza al desmontar
    return () => {
      document.removeEventListener('querySaved', handleQuerySaved);
    };
  }, []);

  const handleDelete = (id_conversation, description) => {
    setSelectedConversation(id_conversation);
    setSelectedConversationName(description || "esta conversación");
    setModalType('conversation');
    setShowModal(true);
  };

  const addConversationGeo = async () => {
    try {
      // Crear el request autenticado
      const authenticatedRequest = await createAuthenticatedRequest(config);

      // Construir la URL
      const url = `${baseURL}${endpoint_add}`;

      // Hacer la petición POST autenticada
      const response = await authenticatedRequest(
        url,
        'POST',
        {
          id_user: user.username,
          type_name: "geo"
        }
      );

      // Verificar la respuesta y navegar
      if (response && response.id_conversation) {
        navigate(`/geo/${response.id_conversation}`);

        // Actualizar la lista de conversaciones
        const newConversation = {
          id_conversation: response.id_conversation,
          description: response.description || "Nueva consulta geo",
          type_name: "geo"
        };

        setConversations(prev => [newConversation, ...prev]);
      } else {
        console.error('No se recibió id_conversation en la respuesta:', response);
      }

    } catch (error) {
      console.error('Error creating new conversation:', error);
      // Aquí podrías agregar un manejo de error más específico
      // Por ejemplo, mostrar un mensaje al usuario
    }
  };

  const deleteConversation = async () => {
    try {
      const authenticatedRequest = await createAuthenticatedRequest(config);
      const url = `${baseURL}${endpoint_delete}`;

      const response = await authenticatedRequest(
        url,
        'DELETE',
        {
          id_conversation: selectedConversation
        }
      );

      if (response) {
        // Actualizar el estado local
        setConversations(prevConversations =>
          prevConversations.filter(convo => convo.id_conversation !== selectedConversation)
        );

        // Cerrar el modal
        setShowModal(false);
        setSelectedConversation(null);
        setSelectedConversationName("");

        // Si estábamos viendo la conversación eliminada, ir a la página principal
        if (activeChatId === selectedConversation) {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Función para manejar la acción del modal de confirmación
  const handleModalAction = () => {
    if (modalType === 'conversation') {
      deleteConversation();
    } else {
      deleteQuery();
    }
  };

  return (
    <div className={`sidebar ${isSidebarVisible ? 'visible' : 'hidden'}`}>
      <div className="sidebar-section logo-section">
        <Link to="/">
          <img src={logo} alt="Logo" className="sidebar-logo" />
        </Link>
      </div>

      <div className="accordion-sections-container">
        {/* Acordeón de Conversaciones Normales */}
        <div className="accordion-section">
          <AccordionHeader
            title="Conversaciones"
            icon={<FaComment size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'conversations'}
            onClick={() => toggleAccordion('conversations')}
          />

          {openAccordion === 'conversations' && (
            <div className="accordion-content">
              <div className="sidebar-link new-chat" onClick={addConversation}>
                <FaPlus size={14} style={{ marginRight: '8px' }} /> Nueva consulta
              </div>

              {isLoading ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loader"></div>
                  <span>Cargando...</span>
                </div>
              ) : (
                normalConversations.length === 0 ? (
                  <div className="no-conversations">
                    <p>No hay conversaciones</p>
                    <p className="no-conversations-sub">Comienza una nueva consulta</p>
                  </div>
                ) : (
                  normalConversations.map((conversation) => (
                    <ConversationLink
                      key={conversation.id_conversation}
                      id_conversation={conversation.id_conversation}
                      type_name={conversation.type_name}
                      description={conversation.description}
                      handleDelete={handleDelete}
                      isActive={conversation.id_conversation === activeChatId}
                    />
                  ))
                )
              )}
            </div>
          )}
        </div>

        {/* Acordeón de Conversaciones Geoespaciales */}
        {/*     <div className="accordion-section">
          <AccordionHeader
            title="Geolocalización"
            icon={<FaGlobe size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'geoConversations'}
            onClick={() => toggleAccordion('geoConversations')}
          />

          {openAccordion === 'geoConversations' && (
            <div className="accordion-content">
              <div className="sidebar-link new-chat" onClick={addConversationGeo}>
                <FaPlus size={14} style={{ marginRight: '8px' }} /> Nueva consulta geo
              </div>

              {isLoading ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loader"></div>
                  <span>Cargando...</span>
                </div>
              ) : (
                geoConversations.length === 0 ? (
                  <div className="no-conversations">
                    <p>No hay consultas geo</p>
                    <p className="no-conversations-sub">Comienza una nueva consulta geoespacial</p>
                  </div>
                ) : (
                  geoConversations.map((conversation) => (
                    <ConversationLink
                      key={conversation.id_conversation}
                      id_conversation={conversation.id_conversation}
                      type_name={conversation.type_name}
                      description={conversation.description}
                      handleDelete={handleDelete}
                      isActive={conversation.id_conversation === activeChatId}
                    />
                  ))
                )
              )}
            </div>
          )}
        </div> */}

        {/* Acordeón de Consultas Guardadas */}
        <div className="accordion-section">
          <AccordionHeader
            title="Consultas Guardadas"
            icon={<FaBookmark size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'savedQueries'}
            onClick={() => toggleAccordion('savedQueries')}
          />

          {openAccordion === 'savedQueries' && (
            <div className="accordion-content">
              {isLoadingQueries ? (
                <div className="sidebar-loading">
                  <div className="sidebar-loader"></div>
                  <span>Cargando consultas...</span>
                </div>
              ) : savedQueries.length === 0 ? (
                <div className="no-items">
                  <p>No hay consultas guardadas</p>
                </div>
              ) : (
                savedQueries.map((queryItem) => (
                  <SavedQueryItem
                    key={queryItem.id_saved}
                    id_saved={queryItem.id_saved}
                    consult={queryItem.consult}
                    onSelect={handleSelectQuery}
                    onDelete={handleDeleteQuery}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* Acordeón de Diccionario de Datos */}
        <div className="accordion-section">
          <AccordionHeader
            title="Diccionario de Datos"
            icon={<FaDatabase size={16} className="accordion-icon" />}
            isOpen={openAccordion === 'dataDictionary'}
            onClick={() => toggleAccordion('dataDictionary')}
          />

          {openAccordion === 'dataDictionary' && (
            <div className="accordion-content">
              {dataDictionary.map((table) => (
                <DataDictionaryItem
                  key={table.tableName}
                  tableName={table.tableName}
                  description={table.description}
                  columns={table.columns}
                  isOpen={openTable === table.tableName}
                  toggleTable={toggleTable}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sección del footer solo con el enlace a Dashboards */}
      {/*       <div className="sidebar-section graficas-bi-section">
        <p className="graficas-bi-text">Pricing Analytics Dashboards ↓</p>
        <a href={UsuariosLink} target="_blank" rel="noopener noreferrer">
          <img src={graficasBI} alt="Gráficas BI" className="graficas-bi-logo" />
        </a>
      </div> */}

      {/* Modal de confirmación */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>
              {modalType === 'conversation'
                ? '¿Desea eliminar la conversación?'
                : '¿Desea eliminar esta consulta guardada?'}
            </p>
            <div className="modal-buttons">
              <button onClick={handleModalAction}>Aceptar</button>
              <button onClick={() => setShowModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarMenu;